import { dateUtil } from "src/app/support/date-util";
// @ts-ignore
import interpretScan from "./gs1/gs1";
import { padStart } from "lodash";
import { sntLog } from "src/app/common/logger";
import { countFlowAction } from "src/app/support/sentry-metrics";

const logger = sntLog("Scanner");

export const MIN_LENGTH = 6;
export const MAX_GAP_MS = 100;
export const MAX_AVG_MS_PER_CHAR = 30;
export const IDLE_TIMEOUT_MS = 100;
export const LATE_TIMER_TOLERANCE_MS = 50;
export const MAX_TIMER_REARMS = 10;

export type ScannerKeyEvent = {
  key: string;
  timeStamp: number;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  repeat: boolean;
  preventDefault: () => void;
  stopImmediatePropagation: () => void;
};

type ScanDetectorOptions = {
  onScan: (scanCode: string) => void;
  now?: () => number;
};

// Timing is measured on KeyboardEvent.timeStamp (when the browser received the
// key), not when our handler runs: under main-thread congestion keydowns queue
// and handlers run far apart even if the scanner was fast.
export class ScanDetector {
  private chars: string[] = [];
  private firstTs = 0;
  private lastTs = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private timerDueAt = 0;
  private rearms = 0;
  private readonly onScan: (scanCode: string) => void;
  private readonly now: () => number;

  constructor({ onScan, now }: ScanDetectorOptions) {
    this.onScan = onScan;
    this.now = now ?? (() => performance.now());
  }

  handleKey(event: ScannerKeyEvent): void {
    if (event.ctrlKey || event.altKey || event.metaKey || event.repeat) {
      return;
    }

    if (event.key === "Enter") {
      this.handleEnter(event);
      return;
    }

    if (event.key.length !== 1) {
      return;
    }

    if (this.chars.length > 0 && event.timeStamp - this.lastTs > MAX_GAP_MS) {
      this.finalize();
    }

    if (this.chars.length === 0) {
      this.firstTs = event.timeStamp;
    }
    this.chars.push(event.key);
    this.lastTs = event.timeStamp;
    this.rearms = 0;
    this.armTimer();
  }

  reset(): void {
    this.clearTimer();
    this.chars = [];
    this.rearms = 0;
  }

  private handleEnter(event: ScannerKeyEvent): void {
    if (this.chars.length === 0) {
      return;
    }

    if (event.timeStamp - this.lastTs > MAX_GAP_MS) {
      this.finalize();
      return;
    }

    if (this.finalize()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  private armTimer(): void {
    this.clearTimer();
    this.timerDueAt = this.now() + IDLE_TIMEOUT_MS;
    this.timer = setTimeout(() => this.onTimer(), IDLE_TIMEOUT_MS);
  }

  private onTimer(): void {
    this.timer = undefined;
    const lateness = this.now() - this.timerDueAt;

    // A late timer means the main thread was busy: queued keydowns may still
    // be pending, so wait before deciding the scan is over.
    if (lateness > LATE_TIMER_TOLERANCE_MS && this.rearms < MAX_TIMER_REARMS) {
      this.rearms++;
      this.armTimer();
      return;
    }

    this.finalize();
  }

  private clearTimer(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  /** Evaluates and clears the buffer. Returns true if a valid scan was emitted. */
  private finalize(): boolean {
    this.clearTimer();

    const length = this.chars.length;
    const duration = this.lastTs - this.firstTs;
    const code = this.chars.join("");
    this.chars = [];
    this.rearms = 0;

    if (length === 0) {
      return false;
    }

    if (length >= MIN_LENGTH && duration <= length * MAX_AVG_MS_PER_CHAR) {
      this.onScan(code);
      return true;
    }

    const avg = duration / length;
    if (length >= MIN_LENGTH && avg <= 2 * MAX_AVG_MS_PER_CHAR) {
      logger.warn(
        `Scan rejected: too slow (length=${length}, duration=${Math.round(duration)}ms, avg=${avg.toFixed(1)}ms/char)`
      );
      countFlowAction("scanner", "scanner", "scan", "error");
    }

    return false;
  }
}

let detector: ScanDetector | undefined;
let currentOnScan: ((scanCode: string) => void) | undefined;

function onKeyDown(event: KeyboardEvent): void {
  detector?.handleKey(event);
}

export function scannerEnable({
  onScan
}: {
  onScan: (scanCode: string) => void;
}): void {
  currentOnScan = onScan;

  if (detector) {
    return;
  }

  detector = new ScanDetector({
    onScan: (scanCode) => currentOnScan?.(scanCode)
  });
  document.addEventListener("keydown", onKeyDown, false);
}

export function scannderDisable(): void {
  if (!detector) {
    return;
  }

  document.removeEventListener("keydown", onKeyDown, false);
  detector.reset();
  detector = undefined;
  currentOnScan = undefined;
}

export function scannerSimulate(scanCode: string): void {
  currentOnScan?.(scanCode);
}

(<any>window)["scannerSimulate"] = scannerSimulate;

export enum SCANNER_AI_VALUES {
  GTIN = "01",
  USE_BY_OR_EXPIRY = "17",
  BATCH_LOT = "10",
  PRICE = "3922",
  NET_WEIGHT = "3923"
}

type ScannerDecodeRawResultItem = {
  ai: SCANNER_AI_VALUES;
  label: string;
  value: string;
};

export type ScannerDecodeResult = {
  upc?: string;
  gtin13?: string;
  gtin14?: string;
  originalGtinIs14: boolean;
  price?: string;
  batchLot?: string;
  useByOrExpiry?: string;
};

export async function scannerDecode(
  scanCode: string
): Promise<ScannerDecodeResult> {
  const resultScan: {
    errmsg?: Error;
    AIbrackets?: string;
    AIfnc1?: string;
    dl?: string;
    licensingMO?: string;
    ol?: ScannerDecodeRawResultItem[];
  } = <any>await interpretScan(scanCode);

  if (resultScan.errmsg) {
    throw resultScan.errmsg;
  }

  const useByOrExpiryRaw = resultScan?.ol?.find(
    (item: ScannerDecodeRawResultItem) =>
      item.ai === SCANNER_AI_VALUES.USE_BY_OR_EXPIRY
  )?.value;

  const useByOrExpiry = useByOrExpiryRaw
    ? dateUtil(useByOrExpiryRaw, "YYYY-MM-DD").isValid()
      ? dateUtil(useByOrExpiryRaw, "YYYY-MM-DD").toISOString()
      : dateUtil(useByOrExpiryRaw, "YYYY-MM").isValid()
        ? dateUtil(useByOrExpiryRaw, "YYYY-MM").toISOString()
        : undefined
    : undefined;

  const gtin = resultScan?.ol?.find(
    (item: ScannerDecodeRawResultItem) => item.ai === SCANNER_AI_VALUES.GTIN
  )?.value!;

  const originalGtinIs14 = scanCode.length === 14;

  const gtin13 = gtin.length === 14 ? gtin.substring(1) : gtin;
  const gtin14 = originalGtinIs14 ? scanCode : padStart(gtin13, 14, "0");

  const upc = scanCode?.length === 12 ? scanCode : undefined;

  return <ScannerDecodeResult>{
    scanCode,
    upc,
    originalGtinIs14,
    gtin13,
    gtin14,
    price: resultScan?.ol?.find(
      (item: ScannerDecodeRawResultItem) => item.ai === SCANNER_AI_VALUES.PRICE
    )?.value,
    batchLot: resultScan?.ol?.find(
      (item: ScannerDecodeRawResultItem) =>
        item.ai === SCANNER_AI_VALUES.BATCH_LOT
    )?.value,
    useByOrExpiry
  };
}
